import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, Settings, LogOut } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/lib/apiClient';
import { Avatar } from '@/features/Profile/Avatar';
import { NotificationBell } from '@/components/NotificationBell';
import { MessageIconButton } from '@/components/MessageIconButton';
import { useUnreadCount } from '@/lib/hooks/useNotifications';
import { useOrgInbox } from '@/lib/hooks/useMessages';
import { useOrgOverview } from '@/lib/hooks/useOrg';
import { OrgCommandPalette } from '@/features/OrgLayout/OrgCommandPalette';

export function OrgTopBar() {
  const { user, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const username = user?.username ?? 'Org Admin';
  const { data: overview } = useOrgOverview();
  const orgName = overview?.org_name ?? 'Organization';
  const unreadCount = useUnreadCount();
  const { data: threads = [] } = useOrgInbox();
  const needsReplyCount = threads.filter((t) => t.last_sender_id === t.student_id).length;

  const handleLogout = async () => {
    try { await apiClient.post('/api/v1/auth/logout'); } catch { /* ignore */ }
    clearAuth();
    navigate({ to: '/' });
  };

  return (
    <header className="flex h-[90px] flex-shrink-0 items-center gap-3 border-b border-[#f1f1f1] bg-white px-4 lg:px-8">
      {/* Which org you're looking at. Lives here rather than in the sidebar,
          which the design keeps to the logo alone. Matters most for super
          admins, who can view any org's routes. */}
      <div className="hidden min-w-0 shrink-0 md:block">
        <p className="text-[10px] font-bold uppercase leading-[15px] tracking-[1.2px] text-[#9ca3af]">
          Organization
        </p>
        <p className="truncate text-[14px] font-semibold leading-[21px] text-[#111827]">
          {orgName}
        </p>
      </div>

      {/* Centered search — opens the org command palette (⌘K) */}
      <div className="flex flex-1 justify-center">
        <OrgCommandPalette />
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <MessageIconButton count={needsReplyCount} to="/org/messages" className="lg:hidden" />

        <NotificationBell unreadCount={unreadCount} />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 outline-none transition-colors hover:bg-muted"
            >
              <Avatar src={user?.avatarUrl} name={username} size={32} />
              <div className="hidden flex-col items-start sm:flex">
                <span className="text-[13px] font-semibold leading-tight text-foreground">{username}</span>
                <span className="text-[11px] leading-tight text-muted-foreground">Org Admin</span>
              </div>
              <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 w-[260px] rounded-xl border border-border bg-background shadow-xl"
              sideOffset={8}
              align="end"
            >
              <div className="flex items-center gap-3 p-4 pb-3">
                <Avatar src={user?.avatarUrl} name={username} size={40} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{username}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              <DropdownMenu.Separator className="h-px bg-border" />
              <div className="p-1.5">
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none hover:bg-muted"
                  onSelect={() => navigate({ to: '/org/settings' })}
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span>Settings</span>
                </DropdownMenu.Item>
              </div>
              <DropdownMenu.Separator className="h-px bg-border" />
              <div className="p-1.5">
                <DropdownMenu.Item
                  onSelect={() => handleLogout()}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-destructive outline-none hover:bg-destructive/10"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
